import { DataType, FormFieldType } from '@doss/prisma/types'
import * as yup from 'yup'
import { JsonValue } from './json.js'

type DefaultValueInput = Record<string, unknown> | JsonValue

export type FormFieldDefaultValuePrimitive = string | number | boolean
export type FormFieldDefaultValueArray = string[]

/**
 * Type for any data that can be used as a default value for a form field
 */
export type FormFieldDefaultValueData = FormFieldDefaultValuePrimitive | FormFieldDefaultValueArray

export const stringDefaultValueSchema = yup.object({ data: yup.string().required() })
export const numberDefaultValueSchema = yup.object({ data: yup.number().required() })
export const booleanDefaultValueSchema = yup.object({ data: yup.boolean().required() })
export const stringArrayDefaultValueSchema = yup.object({
  data: yup.array().of(yup.string().required()).required(),
})

const dateDefaultValueSchema = yup.object({
  data: yup
    .string()
    .test('is-date-or-now', 'Must be valid datetime or "now"', (value) => {
      return value === 'now' || yup.string().datetime().isValidSync(value)
    })
    .required(),
})

const dateTimeDefaultValueSchema = yup.object({
  data: yup
    .string()
    .test(
      'is-date-or-now',
      'Must be valid datetime or "now"',
      (value) => value === 'now' || yup.string().datetime({ allowOffset: true }).isValidSync(value),
    )
    .required(),
})

export const FORM_FIELD_TYPES_THAT_REQUIRE_OPTIONS = [
  FormFieldType.CHECKBOXES,
  FormFieldType.DROPDOWN,
  FormFieldType.RADIO_BUTTON,
]

export const FORM_FIELD_TYPES_THAT_ALLOW_ARRAY: FormFieldType[] = [
  ...FORM_FIELD_TYPES_THAT_REQUIRE_OPTIONS,
  FormFieldType.FILE,
]

/**
 * Checks if a form field default value has a valid value for the type of form field
 */
export function isValidDefaultValue(
  type: FormFieldType,
  defaultValue: Record<string, unknown>,
): FormFieldDefaultValue {
  switch (type) {
    case FormFieldType.BOOLEAN: {
      return booleanDefaultValueSchema.validateSync(defaultValue)
    }
    case FormFieldType.CHECKBOXES: {
      return stringArrayDefaultValueSchema.validateSync(defaultValue)
    }
    case FormFieldType.DATE: {
      return dateDefaultValueSchema.validateSync(defaultValue)
    }
    case FormFieldType.DATE_TIME: {
      return dateTimeDefaultValueSchema.validateSync(defaultValue)
    }
    case FormFieldType.DROPDOWN:
    case FormFieldType.EMAIL:
    case FormFieldType.LOCATION:
    case FormFieldType.LONG_TEXT:
    case FormFieldType.RADIO_BUTTON:
    case FormFieldType.SHORT_TEXT: {
      return stringDefaultValueSchema.validateSync(defaultValue)
    }
    case FormFieldType.NUMBER: {
      return numberDefaultValueSchema.validateSync(defaultValue)
    }
    case FormFieldType.FILE:
      throw new Error('No default values allowed for file type form fields')
    case FormFieldType.SIGNATURE:
      throw new Error('No default values allowed for signature type form fields')
  }
}

export type FormFieldDefaultValue =
  | StringDefaultValue
  | NumberDefaultValue
  | BooleanDefaultValue
  | StringArrayDefaultValue
  | DateDefaultValue
  | DateTimeDefaultValue

export type DateDefaultValue = yup.InferType<typeof dateDefaultValueSchema>
export type DateTimeDefaultValue = yup.InferType<typeof dateTimeDefaultValueSchema>
export type StringDefaultValue = yup.InferType<typeof stringDefaultValueSchema>
export type NumberDefaultValue = yup.InferType<typeof numberDefaultValueSchema>
export type BooleanDefaultValue = yup.InferType<typeof booleanDefaultValueSchema>
export type StringArrayDefaultValue = yup.InferType<typeof stringArrayDefaultValueSchema>

/**
 * Given a form field type, converts to the related data type or inner data type for ARRAY types
 */
export function convertFormFieldToDataType(
  formFieldType: FormFieldType,
  hasLookup: boolean,
): {
  dataType: DataType
  innerType: DataType | null
} {
  let dataType: DataType
  let innerType: DataType | null = null
  switch (formFieldType) {
    case FormFieldType.BOOLEAN:
      dataType = DataType.BOOLEAN
      break
    case FormFieldType.DATE:
      dataType = DataType.DATE
      break
    case FormFieldType.DATE_TIME:
      dataType = DataType.DATE_TIME
      break
    case FormFieldType.SIGNATURE:
    case FormFieldType.FILE:
      dataType = DataType.FILE_URL
      break
    case FormFieldType.NUMBER:
      dataType = DataType.NUMBER
      break
    case FormFieldType.EMAIL:
    case FormFieldType.LOCATION:
    case FormFieldType.SHORT_TEXT:
    case FormFieldType.LONG_TEXT:
      dataType = DataType.STRING
      break
    case FormFieldType.DROPDOWN:
    case FormFieldType.RADIO_BUTTON: {
      if (hasLookup) {
        dataType = DataType.LOOKUP
      } else {
        dataType = DataType.ENUM
      }
      break
    }
    case FormFieldType.CHECKBOXES: {
      dataType = DataType.ARRAY
      if (hasLookup) {
        innerType = DataType.LOOKUP
      } else {
        innerType = DataType.ENUM
      }
      break
    }
  }

  return { dataType, innerType }
}

/** Checks if the data is a valid array default value */
export function isStringArrayDefaultValue(
  data: DefaultValueInput,
): data is StringArrayDefaultValue {
  return stringArrayDefaultValueSchema.isValidSync(data)
}

/** Checks if the data is a valid boolean default value */
export function isBooleanDefaultValue(data: DefaultValueInput): data is BooleanDefaultValue {
  return booleanDefaultValueSchema.isValidSync(data)
}

/** Checks if the data is a valid date default value */
export function isDateDefaultValue(data: DefaultValueInput): data is DateDefaultValue {
  return dateDefaultValueSchema.isValidSync(data)
}

/** Checks if the data is a valid datetime default value */
export function isDateTimeDefaultValue(data: DefaultValueInput): data is DateTimeDefaultValue {
  return dateTimeDefaultValueSchema.isValidSync(data)
}

/** Checks if the data is a valid number default value */
export function isNumberDefaultValue(data: DefaultValueInput): data is NumberDefaultValue {
  return numberDefaultValueSchema.isValidSync(data)
}

/** Checks if the data is a valid string default value */
export function isStringDefaultValue(data: DefaultValueInput): data is StringDefaultValue {
  return stringDefaultValueSchema.isValidSync(data)
}
